import React from "react";
import "./Terms.css";

const sections = [
  { id: "services", number: 1, title: "Our Services" },
  { id: "ip", number: 2, title: "Intellectual Property Rights" },
  { id: "userreps", number: 3, title: "User Representations" },
  { id: "userreg", number: 4, title: "User Registration" },
  { id: "purchases", number: 5, title: "Purchases and Payment" },
  { id: "subscriptions", number: 6, title: "Subscriptions" },
  { id: "prohibited", number: 7, title: "Prohibited Activities" },
  { id: "ugc", number: 8, title: "User-Generated Contributions" },
  { id: "license", number: 9, title: "Contribution License" },
  { id: "sitemanage", number: 10, title: "Services Management" },
  { id: "copyrightno", number: 11, title: "Copyright Infringements" },
  { id: "terms", number: 12, title: "Term and Termination" },
  { id: "modifications", number: 13, title: "Modifications and Interruptions" },
  { id: "law", number: 14, title: "Governing Law" },
  { id: "disputes", number: 15, title: "Dispute Resolution" },
  { id: "corrections", number: 16, title: "Corrections" },
  { id: "disclaimer", number: 17, title: "Disclaimer" },
  { id: "liability", number: 18, title: "Limitations of Liability" },
  { id: "indemnification", number: 19, title: "Indemnification" },
  { id: "userdata", number: 20, title: "User Data" },
  {
    id: "electronic",
    number: 21,
    title: "Electronic Communications, Transactions, and Signatures",
  },
  { id: "misc", number: 22, title: "Miscellaneous" },
  { id: "contact", number: 23, title: "Contact Us" },
];

const Terms = () => {
  return (
    <div className="terms-page">
      <div className="terms-container">
        {/* Header */}
        <header className="terms-header">
          <p className="terms-eyebrow">LEGAL</p>
          <h1>Terms and Conditions</h1>
          <p className="last-updated">
            Last updated: <strong>April 01, 2026</strong>
          </p>
        </header>

        {/* Introduction */}
        <section className="terms-introduction">
          <h2>Agreement to Our Legal Terms</h2>

          <p>
            We are <strong>QIMPENA INDUSTRIES PRIVATE LIMITED</strong> (
            <strong>"Company," "we," "us,"</strong> or{" "}
            <strong>"our"</strong>), a company registered in India at C-25,
            Samrajya Society - 1, Mujmahuda Road, Akota, Vadodara, Gujarat
            390020.
          </p>

          <p>
            We operate the website{" "}
            <a
              href="https://decipherx.in"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://decipherx.in
            </a>{" "}
            (the <strong>"Site"</strong>), as well as any other related
            products and services that refer or link to these legal terms
            (the <strong>"Legal Terms"</strong>), collectively referred to as
            the <strong>"Services"</strong>.
          </p>

          <p>
            At decipherX, we are driven by a passion for simplifying complex
            tasks through intelligent automation. With deep expertise in
            artificial intelligence and machine learning, our focus is on
            delivering robust solutions for audio transcription and image
            CAPTCHA processing that help businesses operate more efficiently
            and securely.
          </p>

          <p>
            Whether you are a startup or an enterprise, we aim to provide
            solutions that are scalable, accurate, and easy to integrate, so
            you can focus on what matters most: your users.
          </p>

          <p>
            You can contact us by email at{" "}
            <a href="mailto:salesnsupport@decipher.in">
              salesnsupport@decipher.in
            </a>{" "}
            or by mail at C-25,
            Samrajya Society - 1, Mujmahuda Road, Akota, Vadodara, Gujarat
            390020, India.
          </p>

          <p>
            These Legal Terms constitute a legally binding agreement between
            you, whether personally or on behalf of an entity (
            <strong>"you"</strong>), and QIMPENA INDUSTRIES PRIVATE LIMITED
            regarding your access to and use of the Services.
          </p>

          <div className="important-notice">
            <strong>IMPORTANT:</strong> By accessing or using the Services,
            you acknowledge that you have read, understood, and agreed to be
            bound by these Legal Terms. If you do not agree with all of these
            Legal Terms, you are expressly prohibited from using the Services
            and must discontinue use immediately.
          </div>

          <p>
            We will provide prior notice of any scheduled changes to the
            Services you are using. Modified Legal Terms will become effective
            upon posting or upon notification to you, including notification
            by email from{" "}
            <a href="mailto:salesnsupport@decipher.in">
              salesnsupport@decipher.in
            </a>
            , as stated in the applicable notice. By continuing to use the
            Services after the effective date of any changes, you agree to be
            bound by the modified Legal Terms.
          </p>

          <p>
            All users who are minors in the jurisdiction in which they reside
            (generally under the age of 18) must have the permission of, and
            be directly supervised by, their parent or legal guardian when
            using the Services. If you are a minor, your parent or guardian
            must read and agree to these Legal Terms before you use the
            Services.
          </p>

          <p>
            We recommend that you print or save a copy of these Legal Terms
            for your records.
          </p>
        </section>

        {/* Table of Contents */}
        <nav className="terms-toc" aria-label="Terms and Conditions sections">
          <div className="toc-heading">
            <span>Contents</span>
            <span className="toc-count">{sections.length} sections</span>
          </div>

          <div className="toc-grid">
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>
                <span className="toc-number">
                  {String(section.number).padStart(2, "0")}
                </span>
                <span>{section.title}</span>
              </a>
            ))}
          </div>
        </nav>

        {/* 1 */}
        <section id="services" className="terms-section">
          <SectionTitle number="1" title="Our Services" />

          <p>
            The information provided when using the Services is not intended
            for distribution to or use by any person or entity in any
            jurisdiction or country where such distribution or use would be
            contrary to applicable law or regulation, or where it would
            subject us to any registration requirement within that
            jurisdiction or country.
          </p>

          <p>
            Accordingly, persons who choose to access the Services from other
            locations do so on their own initiative and are solely responsible
            for compliance with applicable local laws.
          </p>
        </section>

        {/* 2 */}
        <section id="ip" className="terms-section">
          <SectionTitle number="2" title="Intellectual Property Rights" />

          <h3>Our Intellectual Property</h3>

          <p>
            We are the owner or licensee of all intellectual property rights
            in our Services, including all source code, databases,
            functionality, software, website designs, audio, video, text,
            photographs, and graphics contained in the Services
            (collectively, the <strong>"Content"</strong>), as well as the
            trademarks, service marks, and logos contained therein
            (the <strong>"Marks"</strong>).
          </p>

          <p>
            Our Content and Marks are protected by copyright, trademark, and
            other intellectual property and unfair competition laws, as well
            as applicable treaties around the world.
          </p>

          <p>
            The Content and Marks are provided through the Services on an{" "}
            <strong>"AS IS"</strong> basis for your personal, non-commercial
            use or internal business purposes only.
          </p>

          <h3>Your Use of Our Services</h3>

          <p>
            Subject to your compliance with these Legal Terms, including the{" "}
            <a href="#prohibited">Prohibited Activities</a> section below, we
            grant you a non-exclusive, non-transferable, revocable license to:
          </p>

          <ul>
            <li>Access the Services; and</li>
            <li>
              Download or print a copy of any portion of the Content to which
              you have properly gained access.
            </li>
          </ul>

          <p>
            Such use is permitted solely for your personal, non-commercial use
            or internal business purposes.
          </p>

          <p>
            Except as expressly permitted in these Legal Terms, no part of the
            Services and no Content or Marks may be copied, reproduced,
            aggregated, republished, uploaded, posted, publicly displayed,
            encoded, translated, transmitted, distributed, sold, licensed, or
            otherwise exploited for any commercial purpose without our prior
            written permission.
          </p>

          <p>
            If you wish to use the Services, Content, or Marks in a manner not
            expressly permitted by these Legal Terms, please contact us at{" "}
            <a href="mailto:salesnsupport@decipher.in">
              salesnsupport@decipher.in
            </a>
            .
          </p>

          <p>
            If we grant you permission to post, reproduce, or publicly display
            any part of our Services or Content, you must identify us as the
            owner or licensor of the applicable Services, Content, or Marks
            and ensure that all copyright and proprietary notices remain
            visible.
          </p>

          <p>
            We reserve all rights not expressly granted to you in and to the
            Services, Content, and Marks.
          </p>

          <p>
            Any breach of these Intellectual Property Rights provisions will
            constitute a material breach of these Legal Terms, and your right
            to use the Services will terminate immediately.
          </p>

          <h3>Your Submissions and Contributions</h3>

          <p>
            Please carefully review this section and the{" "}
            <a href="#prohibited">Prohibited Activities</a> section before
            using our Services so that you understand the rights you grant us
            and your obligations when posting or uploading content through
            the Services.
          </p>

          <p>
            <strong>Submissions:</strong> By directly sending us any question,
            comment, suggestion, idea, feedback, or other information about
            the Services (collectively, <strong>"Submissions"</strong>), you
            agree to assign to us all intellectual property rights in such
            Submissions.
          </p>

          <p>
            You agree that we shall own such Submissions and may use and
            disseminate them without restriction for any lawful purpose,
            commercial or otherwise, without acknowledgment or compensation
            to you.
          </p>

          <p>
            <strong>Contributions:</strong> The Services may allow you to
            chat, contribute to, or participate in blogs, message boards,
            online forums, or other functionality through which you may
            create, submit, post, display, transmit, publish, distribute, or
            broadcast content and materials.
          </p>

          <p>
            Such materials may include, without limitation, text, writings,
            video, audio, photographs, music, graphics, comments, reviews,
            ratings, suggestions, personal information, or other material
            (collectively, <strong>"Contributions"</strong>). Any Submission
            that is publicly posted will also be treated as a Contribution.
          </p>

          <p>
            You understand that Contributions may be viewable by other users
            of the Services.
          </p>

          <p>
            <strong>
              When you post Contributions, you grant us a license:
            </strong>{" "}
            By posting any Contributions, you grant us an unrestricted,
            unlimited, irrevocable, perpetual, non-exclusive, transferable,
            royalty-free, fully paid, worldwide right and license to use,
            copy, reproduce, distribute, sell, resell, publish, broadcast,
            retitle, store, publicly perform, publicly display, reformat,
            translate, excerpt, and otherwise exploit your Contributions.
          </p>

          <p>
            This license includes, without limitation, the right to use your
            image, name, and voice for commercial, advertising, or other
            lawful purposes; prepare derivative works; incorporate your
            Contributions into other works; and sublicense the rights granted
            under this section. Such use and distribution may occur through
            any media format or channel.
          </p>

          <p>
            This license also includes our use of your name, company name,
            franchise name, trademarks, service marks, trade names, logos, and
            personal or commercial images, as applicable.
          </p>

          <p>
            <strong>You are responsible for what you post or upload.</strong>{" "}
            By sending Submissions or posting Contributions through the
            Services, you:
          </p>

          <ul>
            <li>
              Confirm that you have read and agreed to the{" "}
              <a href="#prohibited">Prohibited Activities</a> section and will
              not submit or post material that is illegal, harassing, hateful,
              harmful, defamatory, obscene, abusive, discriminatory,
              threatening, sexually explicit, false, inaccurate, deceitful, or
              misleading.
            </li>
            <li>
              Waive, to the extent permitted by applicable law, any moral
              rights in your Submissions or Contributions.
            </li>
            <li>
              Warrant that your Submissions and Contributions are original to
              you or that you possess all rights and licenses necessary to
              submit them and grant us the rights described in these Legal
              Terms.
            </li>
            <li>
              Warrant that your Submissions and Contributions do not
              constitute confidential information.
            </li>
          </ul>

          <p>
            You are solely responsible for your Submissions and Contributions
            and agree to reimburse us for losses we may suffer because of your
            breach of this section, infringement of third-party intellectual
            property rights, or violation of applicable law.
          </p>

          <p>
            <strong>We may remove or edit your Content:</strong> Although we
            have no obligation to monitor Contributions, we may remove or edit
            any Contribution at any time without notice if, in our reasonable
            opinion, it is harmful or violates these Legal Terms. We may also
            suspend or disable your account and, when appropriate, report the
            matter to relevant authorities.
          </p>

          <h3>Copyright Infringement</h3>

          <p>
            We respect the intellectual property rights of others. If you
            believe that material available through the Services infringes a
            copyright that you own or control, please refer to the{" "}
            <a href="#copyrightno">Copyright Infringements</a> section below.
          </p>
        </section>

        {/* 3 */}
        <section id="userreps" className="terms-section">
          <SectionTitle number="3" title="User Representations" />

          <p>By using the Services, you represent and warrant that:</p>

          <ol>
            <li>
              All registration information you submit will be true, accurate,
              current, and complete.
            </li>
            <li>
              You will maintain the accuracy of such information and promptly
              update your registration information when necessary.
            </li>
            <li>
              You have the legal capacity to enter into these Legal Terms and
              agree to comply with them.
            </li>
            <li>
              You are not a minor in the jurisdiction in which you reside or,
              if you are a minor, you have received parental or guardian
              permission to use the Services.
            </li>
            <li>
              You will not access the Services through automated or non-human
              means, including through bots or scripts, except where expressly
              authorized by us.
            </li>
            <li>
              You will not use the Services for any illegal or unauthorized
              purpose.
            </li>
            <li>
              Your use of the Services will not violate any applicable law or
              regulation.
            </li>
          </ol>

          <p>
            If you provide information that is untrue, inaccurate, outdated,
            or incomplete, we reserve the right to suspend or terminate your
            account and refuse any current or future use of the Services, or
            any portion thereof.
          </p>
        </section>

        {/* 4 */}
        <section id="userreg" className="terms-section">
          <SectionTitle number="4" title="User Registration" />

          <p>
            You may be required to register to use the Services. You agree to
            keep your password confidential and are responsible for all
            activity conducted through your account and password.
          </p>

          <p>
            We reserve the right to remove, reclaim, or change any username
            you select if we determine, in our sole discretion, that the
            username is inappropriate, obscene, or otherwise objectionable.
          </p>
        </section>

        {/* 5 */}
        <section id="purchases" className="terms-section">
          <SectionTitle number="5" title="Purchases and Payment" />

          <p>We accept the following forms of payment:</p>

          <ul className="payment-list">
            <li>Mastercard</li>
            <li>Visa</li>
            <li>UPI</li>
            <li>Net Banking</li>
          </ul>

          <p>
            You agree to provide current, complete, and accurate purchase and
            account information for all purchases made through the Services.
            You further agree to promptly update your account and payment
            information, including your email address, payment method, and
            payment card expiration date, so that we can complete your
            transactions and contact you as necessary.
          </p>

          <p>
            Applicable taxes may be added to the price of purchases when
            required. We may change prices at any time. All payments shall be
            made in <strong>Indian Rupees (INR)</strong>.
          </p>

          <p>
            You agree to pay all charges at the prices in effect at the time
            of your purchase, together with any applicable fees, and authorize
            us to charge your selected payment provider for such amounts.
          </p>

          <p>
            We reserve the right to correct pricing errors or mistakes,
            including after payment has been requested or received.
          </p>

          <p>
            We reserve the right to refuse any order placed through the
            Services. At our sole discretion, we may limit or cancel
            quantities purchased per person, household, or order. Such
            restrictions may include orders placed through the same customer
            account or payment method, or orders using the same billing or
            shipping address.
          </p>

          <p>
            We also reserve the right to limit or prohibit orders that, in our
            sole judgment, appear to have been placed by dealers, resellers,
            or distributors.
          </p>
        </section>

        {/* 6 */}
        <section id="subscriptions" className="terms-section">
          <SectionTitle number="6" title="Subscriptions" />

          <h3>Billing and Renewal</h3>

          <p>
            Subscriptions do not renew automatically. Customers must manually
            purchase a new subscription to renew their service.
          </p>

          <h3>Free Trial</h3>

          <p>
            We offer a <strong>7-day free trial</strong> to new users who
            register for the Services. A free trial may only be used once.
            After the trial period has ended, users must purchase a production
            subscription to continue using the applicable paid Services.
          </p>

          <h3>Cancellation</h3>

          <p>
            You may cancel your subscription at any time by contacting us
            using the contact information provided below. Your cancellation
            will take effect at the end of the current paid term.
          </p>

          <p>
            If you have any questions or are dissatisfied with our Services,
            please email us at{" "}
            <a href="mailto:salesnsupport@decipher.in">
              salesnsupport@decipher.in
            </a>
            .
          </p>

          <h3>Fee Changes</h3>

          <p>
            We may change subscription fees from time to time and will
            communicate applicable price changes in accordance with
            applicable law.
          </p>
        </section>

        {/* 7 */}
        <section id="prohibited" className="terms-section">
          <SectionTitle number="7" title="Prohibited Activities" />

          <p>
            You may not access or use the Services for any purpose other than
            the purposes for which we make them available. The Services may
            not be used in connection with commercial endeavors except those
            specifically endorsed or approved by us.
          </p>

          <p>As a user of the Services, you agree not to:</p>

          <ul>
            <li>
              Systematically retrieve data or other Content from the Services
              to create or compile a collection, compilation, database, or
              directory without our written permission.
            </li>

            <li>
              Trick, defraud, or mislead us or other users, particularly in an
              attempt to obtain sensitive account information such as
              passwords.
            </li>

            <li>
              Circumvent, disable, or otherwise interfere with
              security-related features of the Services.
            </li>

            <li>
              Disparage, tarnish, or otherwise harm us or the Services, in our
              reasonable opinion.
            </li>

            <li>
              Use information obtained from the Services to harass, abuse, or
              harm another person.
            </li>

            <li>
              Misuse our support services or submit false reports of abuse or
              misconduct.
            </li>

            <li>
              Use the Services in a manner inconsistent with applicable laws
              or regulations.
            </li>

            <li>
              Engage in unauthorized framing of or linking to the Services.
            </li>

            <li>
              Upload or transmit viruses, Trojan horses, malicious code, or
              other material that interferes with another party's use and
              enjoyment of the Services or with the operation, features,
              functionality, or maintenance of the Services.
            </li>

            <li>
              Engage in automated use of the system, such as using scripts to
              send comments or messages, or using data-mining tools, robots,
              or similar data-gathering and extraction tools, except where
              expressly authorized.
            </li>

            <li>
              Delete copyright or other proprietary rights notices from any
              Content.
            </li>

            <li>
              Attempt to impersonate another user or person or use another
              user's username.
            </li>

            <li>
              Upload or transmit material that acts as a passive or active
              information collection or transmission mechanism, including web
              bugs, tracking pixels, or similar devices.
            </li>

            <li>
              Interfere with, disrupt, or create an undue burden on the
              Services or networks connected to the Services.
            </li>

            <li>
              Harass, annoy, intimidate, or threaten employees or agents
              involved in providing any portion of the Services.
            </li>

            <li>
              Attempt to bypass measures designed to prevent or restrict
              access to the Services or any portion thereof.
            </li>

            <li>
              Copy or adapt the Services' software, including HTML,
              JavaScript, PHP, or other code, except where expressly permitted.
            </li>

            <li>
              Except as permitted by applicable law, decipher, decompile,
              disassemble, or reverse engineer software that forms part of the
              Services.
            </li>

            <li>
              Use, launch, develop, or distribute unauthorized automated
              systems, including spiders, robots, scrapers, cheat utilities,
              or offline readers that access the Services, except through
              ordinary search-engine or browser usage.
            </li>

            <li>
              Use a buying agent or purchasing agent to make purchases through
              the Services.
            </li>

            <li>
              Make unauthorized use of the Services, including collecting
              usernames or email addresses for unsolicited communications or
              creating accounts through automated means or under false
              pretenses.
            </li>

            <li>
              Use the Services or Content as part of an effort to compete with
              us or for unauthorized revenue-generating or commercial
              purposes.
            </li>

            <li>Sell or otherwise transfer your profile.</li>

            <li>
              Use the Services to advertise or offer to sell goods or services
              without authorization.
            </li>
          </ul>
        </section>

        {/* 8 */}
        <section id="ugc" className="terms-section">
          <SectionTitle number="8" title="User-Generated Contributions" />

          <p>
            The Services may allow you to chat, contribute to, or participate
            in blogs, message boards, online forums, and other functionality.
            You may have the opportunity to create, submit, post, display,
            transmit, perform, publish, distribute, or broadcast content and
            materials to us or through the Services.
          </p>

          <p>
            Such content may include text, writings, video, audio,
            photographs, graphics, comments, suggestions, personal
            information, or other material (collectively,{" "}
            <strong>"Contributions"</strong>).
          </p>

          <p>
            Contributions may be viewable by other users of the Services and
            through third-party websites. Accordingly, Contributions you
            transmit may be treated as non-confidential and non-proprietary.
          </p>

          <p>
            When you create or make available any Contributions, you represent
            and warrant that:
          </p>

          <ul>
            <li>
              Your Contributions and their creation, distribution,
              transmission, public display, performance, access, downloading,
              or copying do not and will not infringe the proprietary rights
              of any third party, including copyrights, patents, trademarks,
              trade secrets, or moral rights.
            </li>

            <li>
              You are the creator and owner of the Contributions or have all
              necessary licenses, rights, consents, releases, and permissions
              required for their use.
            </li>

            <li>
              You have obtained the written consent, release, or permission of
              every identifiable person included in your Contributions where
              required.
            </li>

            <li>Your Contributions are not false, inaccurate, or misleading.</li>

            <li>
              Your Contributions do not contain unsolicited or unauthorized
              advertising, promotional materials, pyramid schemes, chain
              letters, spam, mass mailings, or other forms of solicitation.
            </li>

            <li>
              Your Contributions are not obscene, lewd, violent, harassing,
              libelous, slanderous, or otherwise objectionable, as reasonably
              determined by us.
            </li>

            <li>
              Your Contributions do not ridicule, mock, disparage, intimidate,
              or abuse another person.
            </li>

            <li>
              Your Contributions are not used to unlawfully harass or threaten
              another person or promote violence against a specific person or
              class of people.
            </li>

            <li>
              Your Contributions do not violate any applicable law,
              regulation, or rule.
            </li>

            <li>
              Your Contributions do not violate the privacy or publicity
              rights of any third party.
            </li>

            <li>
              Your Contributions do not violate applicable laws intended to
              protect children or minors.
            </li>

            <li>
              Your Contributions do not contain offensive comments relating
              to race, national origin, gender, sexual preference, or physical
              disability.
            </li>

            <li>
              Your Contributions do not otherwise violate, or link to material
              that violates, these Legal Terms or applicable law.
            </li>
          </ul>

          <p>
            Any use of the Services in violation of the foregoing constitutes
            a violation of these Legal Terms and may result in suspension or
            termination of your right to use the Services.
          </p>
        </section>

        {/* 9 */}
        <section id="license" className="terms-section">
          <SectionTitle number="9" title="Contribution License" />

          <p>
            By posting Contributions to any part of the Services, you
            automatically grant, and represent and warrant that you have the
            right to grant, to us an unrestricted, unlimited, irrevocable,
            perpetual, non-exclusive, transferable, royalty-free, fully paid,
            worldwide right and license to host, use, copy, reproduce,
            disclose, sell, resell, publish, broadcast, retitle, archive,
            store, cache, publicly perform, publicly display, reformat,
            translate, transmit, excerpt, and distribute those Contributions.
          </p>

          <p>
            This license includes, without limitation, your image and voice and
            permits use for commercial, advertising, or other purposes,
            preparation of derivative works, incorporation into other works,
            and granting and authorization of sublicenses. Such use and
            distribution may occur through any media format or channel.
          </p>

          <p>
            This license applies to any form, media, or technology now known
            or later developed and includes our use of your name, company
            name, franchise name, trademarks, service marks, trade names,
            logos, and personal or commercial images, as applicable.
          </p>

          <p>
            You waive all moral rights in your Contributions to the extent
            permitted by law and warrant that such moral rights have not
            otherwise been asserted.
          </p>

          <p>
            We do not claim ownership of your Contributions. You retain
            ownership of your Contributions and any intellectual property or
            proprietary rights associated with them.
          </p>

          <p>
            We are not liable for statements or representations contained in
            Contributions submitted by you. You are solely responsible for
            your Contributions and agree to release us from responsibility
            regarding them to the extent permitted by applicable law.
          </p>

          <p>
            We have the right, in our sole discretion, to edit, redact,
            re-categorize, pre-screen, or delete Contributions at any time and
            for any reason without notice. We have no obligation to monitor
            Contributions.
          </p>
        </section>

        {/* 10 */}
        <section id="sitemanage" className="terms-section">
          <SectionTitle number="10" title="Services Management" />

          <p>We reserve the right, but not the obligation, to:</p>

          <ol>
            <li>Monitor the Services for violations of these Legal Terms.</li>

            <li>
              Take appropriate legal action against anyone who, in our sole
              discretion, violates applicable law or these Legal Terms,
              including reporting such users to law enforcement authorities.
            </li>

            <li>
              Refuse, restrict, limit, or disable access to any Contribution
              or portion thereof when we consider such action appropriate.
            </li>

            <li>
              Remove or disable files and Content that are excessive in size
              or otherwise burdensome to our systems.
            </li>

            <li>
              Manage the Services in a manner designed to protect our rights
              and property and facilitate the proper functioning of the
              Services.
            </li>
          </ol>
        </section>

        {/* 11 */}
        <section id="copyrightno" className="terms-section">
          <SectionTitle number="11" title="Copyright Infringements" />

          <p>
            We respect the intellectual property rights of others. If you
            believe that material available on or through the Services
            infringes a copyright that you own or control, please immediately
            notify us using the contact information provided below
            (a <strong>"Notification"</strong>).
          </p>

          <p>
            A copy of your Notification may be sent to the person who posted
            or stored the material addressed in the Notification.
          </p>

          <p>
            Please be advised that, under applicable law, you may be liable for
            damages if you make material misrepresentations in a Notification.
            If you are uncertain whether material located on or linked through
            the Services infringes your copyright, you should consider
            consulting a qualified attorney.
          </p>
        </section>

        {/* 12 */}
        <section id="terms" className="terms-section">
          <SectionTitle number="12" title="Term and Termination" />

          <p>
            These Legal Terms shall remain in full force and effect while you
            use the Services.
          </p>

          <div className="legal-emphasis">
            WITHOUT LIMITING ANY OTHER PROVISION OF THESE LEGAL TERMS, WE
            RESERVE THE RIGHT, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR
            LIABILITY, TO DENY ACCESS TO AND USE OF THE SERVICES, INCLUDING BY
            BLOCKING CERTAIN IP ADDRESSES, TO ANY PERSON FOR ANY REASON OR FOR
            NO REASON, INCLUDING FOR BREACH OF ANY REPRESENTATION, WARRANTY,
            OR COVENANT CONTAINED IN THESE LEGAL TERMS OR ANY APPLICABLE LAW
            OR REGULATION.
            <br />
            <br />
            WE MAY TERMINATE YOUR USE OR PARTICIPATION IN THE SERVICES OR
            DELETE YOUR ACCOUNT AND ANY CONTENT OR INFORMATION THAT YOU POSTED
            AT ANY TIME, WITHOUT WARNING, IN OUR SOLE DISCRETION.
          </div>

          <p>
            If we terminate or suspend your account for any reason, you are
            prohibited from registering or creating a new account under your
            name, a false or borrowed name, or the name of any third party,
            even if you are acting on behalf of that third party.
          </p>

          <p>
            In addition to terminating or suspending your account, we reserve
            the right to take appropriate legal action, including pursuing
            civil, criminal, and injunctive remedies.
          </p>
        </section>

        {/* 13 */}
        <section id="modifications" className="terms-section">
          <SectionTitle
            number="13"
            title="Modifications and Interruptions"
          />

          <p>
            We reserve the right to change, modify, or remove the contents of
            the Services at any time and for any reason, at our sole
            discretion, without notice. However, we have no obligation to
            update information provided through the Services.
          </p>

          <p>
            We will not be liable to you or any third party for any
            modification, price change, suspension, or discontinuation of the
            Services.
          </p>

          <p>
            We cannot guarantee that the Services will be available at all
            times. We may experience hardware, software, or other problems or
            may need to perform maintenance related to the Services, resulting
            in interruptions, delays, or errors.
          </p>

          <p>
            We reserve the right to change, revise, update, suspend,
            discontinue, or otherwise modify the Services at any time and for
            any reason without notice.
          </p>

          <p>
            You agree that we shall have no liability for any loss, damage, or
            inconvenience caused by your inability to access or use the
            Services during downtime or discontinuation.
          </p>

          <p>
            Nothing in these Legal Terms shall be construed as requiring us to
            maintain or support the Services or provide corrections, updates,
            or releases.
          </p>
        </section>

        {/* 14 */}
        <section id="law" className="terms-section">
          <SectionTitle number="14" title="Governing Law" />

          <p>
            These Legal Terms shall be governed by and construed in accordance
            with the laws of <strong>India</strong>.
          </p>

          <p>
            You and QIMPENA INDUSTRIES PRIVATE LIMITED irrevocably consent to
            the exclusive jurisdiction of the courts of India for the
            resolution of disputes arising in connection with these Legal
            Terms, subject to the dispute-resolution provisions below.
          </p>
        </section>

        {/* 15 */}
        <section id="disputes" className="terms-section">
          <SectionTitle number="15" title="Dispute Resolution" />

          <h3>Informal Negotiations</h3>

          <p>
            To expedite the resolution of, and control the costs associated
            with, any dispute, controversy, or claim related to these Legal
            Terms (each a <strong>"Dispute"</strong> and collectively, the{" "}
            <strong>"Disputes"</strong>), the parties agree to first attempt
            to negotiate the Dispute informally for at least thirty (30) days
            before initiating arbitration, except for the Disputes expressly
            excluded below.
          </p>

          <p>
            Informal negotiations shall begin upon written notice from one
            party to the other.
          </p>

          <h3>Binding Arbitration</h3>

          <p>
            Any dispute arising out of or in connection with these Legal
            Terms, including any question regarding their existence, validity,
            or termination, shall be referred to and finally resolved by the
            International Commercial Arbitration Court under the European
            Arbitration Chamber (Belgium, Brussels, Avenue Louise, 146) in
            accordance with the Rules of the ICAC, which are deemed
            incorporated into this clause by reference.
          </p>

          <p>
            The number of arbitrators shall be <strong>zero (0)</strong>. The
            seat, or legal place, of arbitration shall be{" "}
            <strong>Gujarat, India</strong>. The language of the proceedings
            shall be <strong>English</strong>. The governing substantive law
            shall be the law of <strong>India</strong>.
          </p>

          <h3>Restrictions</h3>

          <p>
            The parties agree that any arbitration shall be limited to the
            Dispute between the parties individually. To the fullest extent
            permitted by law:
          </p>

          <ol type="a">
            <li>
              No arbitration shall be joined with any other proceeding.
            </li>
            <li>
              No Dispute may be arbitrated on a class-action basis or through
              class-action procedures.
            </li>
            <li>
              No Dispute may be brought in a representative capacity on behalf
              of the general public or any other persons.
            </li>
          </ol>

          <h3>Exceptions to Informal Negotiations and Arbitration</h3>

          <p>
            The parties agree that the following Disputes are not subject to
            the provisions above concerning informal negotiations and binding
            arbitration:
          </p>

          <ol type="a">
            <li>
              Disputes seeking to enforce or protect, or concerning the
              validity of, intellectual property rights of either party.
            </li>
            <li>
              Disputes related to or arising from allegations of theft,
              piracy, invasion of privacy, or unauthorized use.
            </li>
            <li>Claims for injunctive relief.</li>
          </ol>

          <p>
            If any portion of this provision is found to be illegal or
            unenforceable, neither party shall be required to arbitrate a
            Dispute falling within that portion. Such Dispute shall instead be
            decided by a court of competent jurisdiction within the
            jurisdiction identified above, and the parties agree to submit to
            that court's personal jurisdiction.
          </p>
        </section>

        {/* 16 */}
        <section id="corrections" className="terms-section">
          <SectionTitle number="16" title="Corrections" />

          <p>
            Information provided through the Services may contain
            typographical errors, inaccuracies, or omissions, including errors
            relating to descriptions, pricing, availability, and other
            information.
          </p>

          <p>
            We reserve the right to correct any errors, inaccuracies, or
            omissions and to change or update information provided through the
            Services at any time without prior notice.
          </p>
        </section>

        {/* 17 */}
        <section id="disclaimer" className="terms-section">
          <SectionTitle number="17" title="Disclaimer" />

          <div className="legal-emphasis">
            THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS.
            YOU AGREE THAT YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK.
            <br />
            <br />
            TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
            WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES
            AND YOUR USE THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            AND NON-INFRINGEMENT.
            <br />
            <br />
            WE MAKE NO WARRANTIES OR REPRESENTATIONS REGARDING THE ACCURACY OR
            COMPLETENESS OF THE SERVICES' CONTENT OR THE CONTENT OF ANY
            WEBSITES OR MOBILE APPLICATIONS LINKED TO THE SERVICES.
          </div>

          <p>We assume no liability or responsibility for:</p>

          <ol>
            <li>Errors, mistakes, or inaccuracies in Content and materials.</li>

            <li>
              Personal injury or property damage of any nature resulting from
              access to or use of the Services.
            </li>

            <li>
              Unauthorized access to or use of our secure servers or personal
              or financial information stored therein.
            </li>

            <li>
              Interruptions or cessation of transmission to or from the
              Services.
            </li>

            <li>
              Bugs, viruses, Trojan horses, or similar harmful components
              transmitted through the Services by third parties.
            </li>

            <li>
              Errors or omissions in Content or materials, or losses or
              damages resulting from the use of Content posted, transmitted,
              or otherwise made available through the Services.
            </li>
          </ol>

          <p>
            We do not warrant, endorse, guarantee, or assume responsibility for
            any product or service advertised or offered by a third party
            through the Services, any hyperlinked website, or any website or
            mobile application featured in advertising.
          </p>

          <p>
            We will not be a party to or responsible for monitoring
            transactions between you and third-party providers of products or
            services. As with any purchase made through any medium, you should
            exercise appropriate judgment and caution.
          </p>
        </section>

        {/* 18 */}
        <section id="liability" className="terms-section">
          <SectionTitle number="18" title="Limitations of Liability" />

          <div className="legal-emphasis">
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE
            LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT,
            CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE
            DAMAGES, INCLUDING LOST PROFITS, LOST REVENUE, LOSS OF DATA, OR
            OTHER DAMAGES ARISING FROM YOUR USE OF THE SERVICES, EVEN IF WE
            HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </div>
        </section>

        {/* 19 */}
        <section id="indemnification" className="terms-section">
          <SectionTitle number="19" title="Indemnification" />

          <p>
            You agree to defend, indemnify, and hold us harmless, including our
            subsidiaries, affiliates, officers, agents, partners, and
            employees, from and against any loss, damage, liability, claim, or
            demand, including reasonable attorneys' fees and expenses, made
            by any third party due to or arising out of:
          </p>

          <ol>
            <li>Your Contributions.</li>
            <li>Your use of the Services.</li>
            <li>Your breach of these Legal Terms.</li>
            <li>
              Any breach of your representations and warranties contained in
              these Legal Terms.
            </li>
            <li>
              Your violation of the rights of a third party, including
              intellectual property rights.
            </li>
            <li>
              Any overt harmful act toward another user of the Services with
              whom you connected through the Services.
            </li>
          </ol>

          <p>
            Notwithstanding the foregoing, we reserve the right, at your
            expense, to assume the exclusive defense and control of any matter
            for which you are required to indemnify us. You agree to
            cooperate, at your expense, with our defense of such claims.
          </p>

          <p>
            We will use reasonable efforts to notify you of any claim, action,
            or proceeding subject to this indemnification once we become aware
            of it.
          </p>
        </section>

        {/* 20 */}
        <section id="userdata" className="terms-section">
          <SectionTitle number="20" title="User Data" />

          <p>
            We will maintain certain data that you transmit to the Services
            for the purpose of managing the performance of the Services, as
            well as data relating to your use of the Services.
          </p>

          <p>
            Although we perform routine backups of data, you are solely
            responsible for all data that you transmit or that relates to
            activities you undertake using the Services.
          </p>

          <p>
            You agree that, to the extent permitted by applicable law, we
            shall have no liability to you for any loss or corruption of such
            data, and you waive any right of action against us arising from
            such loss or corruption.
          </p>
        </section>

        {/* 21 */}
        <section id="electronic" className="terms-section">
          <SectionTitle
            number="21"
            title="Electronic Communications, Transactions, and Signatures"
          />

          <p>
            Visiting the Services, sending us emails, and completing online
            forms constitute electronic communications.
          </p>

          <p>
            You consent to receive electronic communications and agree that
            all agreements, notices, disclosures, and other communications we
            provide to you electronically, including through email and the
            Services, satisfy any legal requirement that such communications
            be in writing.
          </p>

          <div className="legal-emphasis">
            YOU AGREE TO THE USE OF ELECTRONIC SIGNATURES, CONTRACTS, ORDERS,
            AND OTHER RECORDS, AND TO THE ELECTRONIC DELIVERY OF NOTICES,
            POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR COMPLETED BY US
            OR THROUGH THE SERVICES.
          </div>

          <p>
            To the extent permitted by applicable law, you waive any rights or
            requirements under statutes, regulations, rules, ordinances, or
            other laws that require an original signature or the delivery or
            retention of non-electronic records, or that require payments or
            the granting of credits by means other than electronic methods.
          </p>
        </section>

        {/* 22 */}
        <section id="misc" className="terms-section">
          <SectionTitle number="22" title="Miscellaneous" />

          <p>
            These Legal Terms and any policies or operating rules posted by us
            through the Services constitute the entire agreement and
            understanding between you and us.
          </p>

          <p>
            Our failure to exercise or enforce any right or provision of these
            Legal Terms shall not operate as a waiver of that right or
            provision.
          </p>

          <p>
            These Legal Terms operate to the fullest extent permitted by law.
            We may assign any or all of our rights and obligations to others
            at any time.
          </p>

          <p>
            We shall not be responsible or liable for any loss, damage, delay,
            or failure to act caused by circumstances beyond our reasonable
            control.
          </p>

          <p>
            If any provision or portion of a provision of these Legal Terms is
            determined to be unlawful, void, or unenforceable, that provision
            or portion shall be deemed severable and shall not affect the
            validity or enforceability of the remaining provisions.
          </p>

          <p>
            No joint venture, partnership, employment, or agency relationship
            is created between you and us as a result of these Legal Terms or
            your use of the Services.
          </p>

          <p>
            You agree that these Legal Terms shall not be construed against us
            merely because we drafted them.
          </p>

          <p>
            To the extent permitted by applicable law, you waive any defenses
            based on the electronic form of these Legal Terms or the absence
            of physical signatures by the parties.
          </p>
        </section>

        {/* 23 */}
        <section id="contact" className="terms-section">
          <SectionTitle number="23" title="Contact Us" />

          <p>
            To resolve a complaint regarding the Services or to receive
            further information about the use of the Services, please contact
            us at:
          </p>

          <div className="contact-card">
            <strong>QIMPENA INDUSTRIES PRIVATE LIMITED</strong>

            <span>C-25, Samrajya Society - 1</span>
            <span>Mujmahuda Road, Akota</span>
            <span>Vadodara, Gujarat, 390020</span>
            <span>India</span>

            <a href="mailto:salesnsupport@decipher.in">
              salesnsupport@decipher.in
            </a>
          </div>
        </section>

        <footer className="terms-footer">
          <p>
            © {new Date().getFullYear()} decipherX. All rights reserved.
          </p>

          <a href="#top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top ↑
          </a>
        </footer>
      </div>
    </div>
  );
};

const SectionTitle = ({ number, title }) => (
  <div className="section-title">
    <span className="section-number">{String(number).padStart(2, "0")}</span>
    <h2>{title}</h2>
  </div>
);

export default Terms;